<script setup>
import { $api } from '@/utils/api'
import { nextTick, onMounted, ref, computed, watch } from 'vue'

definePage({
  meta: {
    layout: 'default',
    requiresAuth: true,
    roles: ['secretary_anesthetist'],
  },
})

const activeTab = ref('contacts')
const snackbar = ref({ show: false, text: '', color: 'success' })
const notify = (text, color = 'success') => { snackbar.value = { show: true, text, color } }

const normalizePhone = value => {
  if (typeof value !== 'string') return value?.value ?? ''

  try {
    const contact = JSON.parse(value)

    return ['phone', 'mobile'].includes(contact?.type) && typeof contact?.value === 'string'
      ? contact.value
      : value
  } catch {
    return value
  }
}

const shadowPayload = row => ({ ...row, phone: normalizePhone(row.phone) })

// ─── CONTACTS TAB ─────────────────────────────────────────────────────────────

const contactRows = ref([])
const contactsLoading = ref(false)
const contactSaving = ref({})
const contactDeleting = ref({})
const contactEditingCell = ref(null)
const contactSearch = ref('')
const expandedContactIds = ref([])

const contactColumns = [
  { key: 'first_name', label: 'Office / Name', width: '160px', required: true },
  { key: 'last_name',  label: 'Last Name',     width: '140px' },
  { key: 'email',      label: 'Email',          width: '210px', required: true },
  { key: 'phone',      label: 'Phone',          width: '140px' },
  { key: 'notes',      label: 'Notes',          width: '220px' },
]

const isReadOnly = row => row.type === 'registered'
const isContactExpanded = row => expandedContactIds.value.includes(row._key)

const toggleContactExpanded = row => {
  if (isContactExpanded(row)) {
    expandedContactIds.value = expandedContactIds.value.filter(id => id !== row._key)
    
    return
  }

  expandedContactIds.value = [...expandedContactIds.value, row._key]
}

const newContactRow = () => ({
  _key: `new-${Date.now()}`,
  _isNew: true,
  _dirty: true,
  id: null,
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  notes: '',
})

const fetchContacts = async (search = '') => {
  contactsLoading.value = true
  try {
    const params = search ? `?search=${encodeURIComponent(search)}` : ''
    const res = await $api(`/api/control-tower/shadow-surgeon-secretaries${params}`)

    contactRows.value = (res?.data ?? []).map(r => ({
      ...r,
      phone: normalizePhone(r.phone),
      type: 'internal',
      surgeons: Array.isArray(r.surgeons) ? r.surgeons : [],
      _key: String(r.id),
      _isNew: false,
      _dirty: false,
    }))
  } catch {
    notify('Failed to load contacts', 'error')
  } finally {
    contactsLoading.value = false
  }
}

let searchTimer = null
watch(contactSearch, val => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => fetchContacts(val), 350)
})

const addContactRow = async () => {
  const row = newContactRow()

  contactRows.value.push(row)
  await nextTick()
  contactEditingCell.value = { rowKey: row._key, field: 'first_name' }
}

const isContactEditing = (row, field) =>
  contactEditingCell.value?.rowKey === row._key && contactEditingCell.value?.field === field

const startContactEdit = (row, field) => {
  contactEditingCell.value = { rowKey: row._key, field }
}

const stopContactEdit = () => { contactEditingCell.value = null }

const saveContactRow = async row => {
  if (!row._dirty) return
  if (!row.first_name) { notify('Office/Name is required', 'error') 

    return }
  if (!row.email) { notify('Email is required', 'error') 

    return }

  contactSaving.value[row._key] = true
  stopContactEdit()
  try {
    if (row._isNew) {
      const res = await $api('/api/control-tower/shadow-surgeon-secretaries', {
        method: 'POST',
        body: JSON.stringify(shadowPayload(row)),
      })

      const saved = res?.data
      if (saved) {
        const idx = contactRows.value.findIndex(r => r._key === row._key)

        contactRows.value[idx] = { ...saved, _key: String(saved.id), _isNew: false, _dirty: false }
      }
    } else {
      const res = await $api(`/api/control-tower/shadow-surgeon-secretaries/${row.id}`, {
        method: 'PUT',
        body: JSON.stringify(shadowPayload(row)),
      })

      const saved = res?.data
      if (saved) {
        const idx = contactRows.value.findIndex(r => r.id === row.id)

        contactRows.value[idx] = { ...saved, _key: String(saved.id), _isNew: false, _dirty: false }
      }
    }
    notify('Contact saved')
  } catch {
    notify('Save failed', 'error')
    row._dirty = true
  } finally {
    delete contactSaving.value[row._key]
  }
}

const deleteContactRow = async row => {
  if (row._isNew) { contactRows.value = contactRows.value.filter(r => r._key !== row._key) 

    return }
  if (!window.confirm(`Delete ${row.first_name} ${row.last_name}? This action cannot be undone.`)) return
  contactDeleting.value[row._key] = true
  try {
    await $api(`/api/control-tower/shadow-surgeon-secretaries/${row.id}`, { method: 'DELETE' })
    contactRows.value = contactRows.value.filter(r => r._key !== row._key)
    notify('Contact deleted')
  } catch {
    notify('Delete failed', 'error')
  } finally {
    delete contactDeleting.value[row._key]
  }
}

const contactDirtyCount = computed(() => contactRows.value.filter(r => r._dirty).length)

// ─── SURGEONS TAB ───────────────────────────────────────────────────────────

const surgeonRows = ref([])
const surgeonsLoading = ref(false)
const surgeonSearch = ref('')
const surgeonSaving = ref({})
const surgeonDeleting = ref({})
const surgeonEditingCell = ref(null)

const surgeonColumns = [
  { key: 'first_name', label: 'First name', required: true },
  { key: 'last_name', label: 'Last name', required: true },
  { key: 'gmc_number', label: 'GMC number', required: true },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
]

const fetchSurgeons = async (search = '') => {
  surgeonsLoading.value = true
  try {
    const query = search ? `?search=${encodeURIComponent(search)}` : ''
    const res = await $api(`/api/control-tower/shadow-surgeons${query}`)

    surgeonRows.value = (res?.data ?? []).map(row => ({ ...row, phone: normalizePhone(row.phone), _key: String(row.id), _isNew: false, _dirty: false }))
  } catch {
    notify('Failed to load surgeons', 'error')
  } finally {
    surgeonsLoading.value = false
  }
}

let surgeonSearchTimer = null
watch(surgeonSearch, val => {
  clearTimeout(surgeonSearchTimer)
  surgeonSearchTimer = setTimeout(() => fetchSurgeons(val), 350)
})

const addSurgeonRow = async () => {
  await fetchLookups()

  const row = { _key: `new-${Date.now()}`, _isNew: true, _dirty: true, first_name: '', last_name: '', gmc_number: '', email: '', phone: '', specialist_area_ids: [] }

  surgeonRows.value.push(row)
  await nextTick()
  surgeonEditingCell.value = { rowKey: row._key, field: 'first_name' }
}

const isSurgeonEditing = (row, field) => surgeonEditingCell.value?.rowKey === row._key && surgeonEditingCell.value?.field === field
const startSurgeonEdit = (row, field) => { surgeonEditingCell.value = { rowKey: row._key, field } }
const stopSurgeonEdit = () => { surgeonEditingCell.value = null }

const saveSurgeonRow = async row => {
  if (!row._dirty) return
  if (!row.first_name || !row.last_name || !row.gmc_number) { notify('First name, last name and GMC number are required', 'error') 

    return }
  surgeonSaving.value[row._key] = true
  stopSurgeonEdit()
  try {
    const res = await $api(row._isNew ? '/api/control-tower/shadow-surgeons' : `/api/control-tower/shadow-surgeons/${row.id}`, { method: row._isNew ? 'POST' : 'PUT', body: JSON.stringify(shadowPayload(row)) })
    const saved = res?.data
    const index = surgeonRows.value.findIndex(current => current._key === row._key)
    if (saved && index !== -1) surgeonRows.value[index] = { ...saved, _key: String(saved.id), _isNew: false, _dirty: false }
    notify('Surgeon saved')
  } catch { row._dirty = true; notify('Save failed', 'error') } finally { delete surgeonSaving.value[row._key] }
}

const deleteSurgeonRow = async row => {
  if (row._isNew) { surgeonRows.value = surgeonRows.value.filter(current => current._key !== row._key) 

    return }
  if (!window.confirm(`Delete ${row.first_name} ${row.last_name}? This action cannot be undone.`)) return
  surgeonDeleting.value[row._key] = true
  try { await $api(`/api/control-tower/shadow-surgeons/${row.id}`, { method: 'DELETE' }); surgeonRows.value = surgeonRows.value.filter(current => current._key !== row._key); notify('Surgeon deleted') } catch { notify('Delete failed', 'error') } finally { delete surgeonDeleting.value[row._key] }
}

// ─── QUICK LISTINGS TAB ───────────────────────────────────────────────────────

const listingRows = ref([])
const listingLoading = ref(false)
const listingSaving = ref({})
const listingEditingCell = ref(null)

// Lookup data
const hospitals = ref([])
const specialistAreas = ref([])
const managedAnaesthetists = ref([])
const lookupsLoaded = ref(false)

const fetchLookups = async () => {
  if (lookupsLoaded.value) return
  listingLoading.value = true
  try {
    const [hRes, aRes, anRes] = await Promise.all([
      $api('/api/v1/hospitals/autocomplete'),
      $api('/api/v1/specialist-areas/autocomplete'),
      $api('/api/secretary-anesthetists/anaesthetists'),
    ])

    hospitals.value = hRes?.data ?? []
    specialistAreas.value = aRes?.data ?? []
    managedAnaesthetists.value = (anRes?.data ?? []).filter(a => a.is_active)
    lookupsLoaded.value = true
  } catch {
    notify('Failed to load lookup data', 'error')
  } finally {
    listingLoading.value = false
  }
}

const newListingRow = () => ({
  _key: `new-${Date.now()}`,
  _isNew: true,
  _dirty: true,
  _saving: false,
  _saved: false,
  surgeon_gmc_number: '',
  hospital_id: null,
  specialist_area_ids: [],
  scheduled_date: '',
  scheduled_time: '08:00',
  duration_minutes: 120,
  assigned_anaesthetist_id: null,
})

const addListingRow = async () => {
  await fetchLookups()

  const row = newListingRow()

  listingRows.value.push(row)
}

const removeListingRow = key => {
  listingRows.value = listingRows.value.filter(r => r._key !== key)
}

const hospitalName = id => hospitals.value.find(h => h.id === id)?.name ?? '—'
const anaesthetistName = id => managedAnaesthetists.value.find(a => a.id === id)?.full_name ?? '—'

const saveListingRow = async row => {
  if (!row.surgeon_gmc_number && !row.hospital_id) {
    notify('Hospital is required', 'error')
    
    return
  }
  if (!row.scheduled_date) {
    notify('Date is required', 'error')
    
    return
  }

  row._saving = true
  try {
    const payload = {
      hospital_id: row.hospital_id,
      scheduled_at: `${row.scheduled_date}T${row.scheduled_time}:00`,
      specialist_area_ids: row.specialist_area_ids,
      duration_minutes: row.duration_minutes,
      visibility: 'private',
    }

    if (row.surgeon_gmc_number) payload.surgeon_gmc_number = row.surgeon_gmc_number
    if (row.assigned_anaesthetist_id) payload.assigned_anaesthetist_id = row.assigned_anaesthetist_id

    await $api('/api/control-tower/listings', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
    row._saved = true
    row._dirty = false
    notify('Listing created')

    // Remove from grid after short delay
    setTimeout(() => removeListingRow(row._key), 1200)
  } catch {
    notify('Failed to create listing', 'error')
  } finally {
    row._saving = false
  }
}

// Load contacts on mount; listings tab loads lazily
onMounted(fetchContacts)
</script>

<template>
  <div class="external-contacts-page">
    <!-- Header -->
    <VCard class="mb-4">
      <VCardText>
        <div class="d-flex align-center justify-space-between ga-4">
          <div>
            <h2 class="text-h4 mb-2">
              External Management
            </h2>
            <p class="text-body-2 text-medium-emphasis">
              Manage off-platform contacts and quickly register internal listings.
            </p>
          </div>
          <VIcon
            size="x-large"
            color="primary"
          >
            mdi-account-network-outline
          </VIcon>
        </div>
      </VCardText>
    </VCard>

    <VTabs
      v-model="activeTab"
      class="mb-4"
    >
      <VTab value="contacts">
        <VIcon
          icon="mdi-contacts-outline"
          size="16"
          class="me-1"
        />
        Surgeon Secretaries
      </VTab>
      <VTab
        value="surgeons"
        @click="fetchSurgeons(surgeonSearch); fetchLookups()"
      >
        <VIcon
          icon="mdi-doctor"
          size="16"
          class="me-1"
        />
        Surgeons
      </VTab>
      <VTab
        value="listings"
        @click="fetchLookups"
      >
        <VIcon
          icon="mdi-calendar-plus-outline"
          size="16"
          class="me-1"
        />
        Quick Listings
      </VTab>
    </VTabs>

    <VWindow
      v-model="activeTab"
      :touch="false"
    >
      <!-- ─── TAB 1: CONTACTS ──────────────────────────────────────────────── -->
      <VWindowItem value="contacts">
        <VCard>
          <!-- Toolbar ricerca -->
          <div class="d-flex align-center px-3 py-2 ga-3">
            <VTextField
              v-model="contactSearch"
              density="compact"
              variant="outlined"
              hide-details
              placeholder="Search by first or last name…"
              prepend-inner-icon="mdi-magnify"
              clearable
              style="max-width: 320px"
            />
            <VSpacer />
            <VBtn
              color="primary"
              variant="flat"
              prepend-icon="mdi-plus"
              @click="addContactRow"
            >
              Add Contact
            </VBtn>
          </div>

          <VDivider />

          <VCardText class="pa-0">
            <div
              v-if="contactsLoading"
              class="d-flex justify-center pa-8"
            >
              <VProgressCircular
                indeterminate
                color="primary"
              />
            </div>

            <div
              v-else
              class="grid-wrapper"
            >
              <table class="data-grid">
                <thead>
                  <tr>
                    <th class="col-expand" />
                    <th style="min-width: 100px">
                      Record type
                    </th>
                    <th
                      v-for="col in contactColumns"
                      :key="col.key"
                      :style="{ minWidth: col.width }"
                    >
                      {{ col.label }}<span
                        v-if="col.required"
                        class="text-error"
                      > *</span>
                    </th>
                    <th class="col-actions" />
                  </tr>
                </thead>
                <tbody>
                  <template
                    v-for="row in contactRows"
                    :key="row._key"
                  >
                    <tr :class="{ 'row--dirty': row._dirty, 'row--new': row._isNew, 'row--registered': row.type === 'registered' }">
                      <td class="col-expand">
                        <VBtn
                          v-if="row.surgeons?.length"
                          :icon="isContactExpanded(row) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                          size="small"
                          variant="text"
                          :title="isContactExpanded(row) ? 'Hide linked surgeons' : 'Show linked surgeons'"
                          @click.stop="toggleContactExpanded(row)"
                        />
                      </td>
                      <td class="grid-cell">
                        <VChip
                          :color="row.type === 'registered' ? 'primary' : 'secondary'"
                          size="x-small"
                          variant="tonal"
                          label
                        >
                          {{ row.type }}
                        </VChip>
                      </td>
                      <td
                        v-for="col in contactColumns"
                        :key="col.key"
                        class="grid-cell"
                        @click="!isReadOnly(row) && startContactEdit(row, col.key)"
                      >
                        <input
                          v-if="!isReadOnly(row) && isContactEditing(row, col.key)"
                          :ref="el => el && el.focus()"
                          v-model="row[col.key]"
                          class="cell-input"
                          :type="col.key === 'email' ? 'email' : 'text'"
                          @input="row._dirty = true"
                          @blur="stopContactEdit"
                          @keydown.enter="stopContactEdit"
                          @keydown.escape="stopContactEdit"
                        >
                        <span
                          v-else
                          class="cell-value"
                          :class="{ 'cell-value--empty': !row[col.key] }"
                        >
                          {{ row[col.key] || (col.required ? '(required)' : '—') }}
                        </span>
                      </td>
                      <td class="col-actions">
                        <template v-if="!isReadOnly(row)">
                          <VBtn
                            v-if="row._dirty"
                            icon
                            size="small"
                            variant="text"
                            color="success"
                            :loading="contactSaving[row._key]"
                            @click.stop="saveContactRow(row)"
                          >
                            <VIcon size="18">
                              mdi-content-save-outline
                            </VIcon>
                          </VBtn>
                          <VBtn
                            icon
                            size="small"
                            variant="text"
                            color="error"
                            :loading="contactDeleting[row._key]"
                            @click.stop="deleteContactRow(row)"
                          >
                            <VIcon size="18">
                              mdi-delete-outline
                            </VIcon>
                          </VBtn>
                        </template>
                      </td>
                    </tr>
                    <tr
                      v-if="isContactExpanded(row)"
                      class="expanded-row"
                    >
                      <td :colspan="contactColumns.length + 3">
                        <div class="expanded-content">
                          <div class="text-subtitle-2 mb-2">
                            Linked surgeons
                          </div>
                          <VTable density="compact">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>GMC number</th>
                                <th>Profile</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr
                                v-for="surgeon in row.surgeons"
                                :key="surgeon.id"
                              >
                                <td>{{ surgeon.full_name || '—' }}</td>
                                <td>{{ surgeon.gmc_number || '—' }}</td>
                                <td>
                                  <VChip
                                    size="x-small"
                                    :color="surgeon.is_registered ? 'primary' : 'secondary'"
                                    variant="tonal"
                                  >
                                    {{ surgeon.is_registered ? 'Registered' : 'Shadow' }}
                                  </VChip>
                                </td>
                              </tr>
                            </tbody>
                          </VTable>
                        </div>
                      </td>
                    </tr>
                  </template>
                  <tr v-if="contactRows.length === 0">
                    <td
                      :colspan="contactColumns.length + 3"
                      class="text-center text-medium-emphasis pa-8"
                    >
                      No contacts found.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </VCardText>

          <VDivider />

          <VAlert
            type="info"
            variant="tonal"
            density="compact"
            class="ma-3"
          >
            <strong>Record type:</strong> <em>internal</em> is a shadow surgeon-secretary created by your group and can be edited; <em>registered</em> has a platform account and is read-only here.
          </VAlert>

          <VCardActions class="pa-3 d-flex justify-space-between">
            <VAlert
              v-if="contactDirtyCount > 0"
              type="info"
              variant="tonal"
              density="compact"
              class="py-1 px-3"
              style="font-size:0.8rem"
            >
              {{ contactDirtyCount }} unsaved row{{ contactDirtyCount > 1 ? 's' : '' }} — click
              <VIcon
                size="13"
                color="success"
              >
                mdi-content-save-outline
              </VIcon> to save each one
            </VAlert>
            <VSpacer />
            <VBtn
              color="primary"
              variant="flat"
              prepend-icon="mdi-plus"
              @click="addContactRow"
            >
              Add Contact
            </VBtn>
          </VCardActions>
        </VCard>
      </VWindowItem>

      <!-- ─── TAB 2: SURGEONS ──────────────────────────────────────────────── -->
      <VWindowItem value="surgeons">
        <VCard>
          <div class="d-flex align-center px-3 py-2 ga-3">
            <VTextField
              v-model="surgeonSearch"
              density="compact"
              variant="outlined"
              hide-details
              placeholder="Search by name or GMC number…"
              prepend-inner-icon="mdi-magnify"
              clearable
              style="max-width: 320px"
            />
            <VSpacer />
            <VBtn
              color="primary"
              variant="flat"
              prepend-icon="mdi-plus"
              @click="addSurgeonRow"
            >
              Add Surgeon
            </VBtn>
          </div>
          <VDivider />
          <VCardText class="pa-0">
            <div
              v-if="surgeonsLoading"
              class="d-flex justify-center pa-8"
            >
              <VProgressCircular
                indeterminate
                color="primary"
              />
            </div>
            <div
              v-else
              class="grid-wrapper"
            >
              <table class="data-grid">
                <thead>
                  <tr>
                    <th
                      v-for="column in surgeonColumns"
                      :key="column.key"
                    >
                      {{ column.label }}<span
                        v-if="column.required"
                        class="text-error"
                      > *</span>
                    </th>
                    <th>Specialist areas</th>
                    <th>Surgeon secretaries</th>
                    <th class="col-actions" />
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="surgeon in surgeonRows"
                    :key="surgeon._key"
                    :class="{ 'row--dirty': surgeon._dirty, 'row--new': surgeon._isNew }"
                  >
                    <td
                      v-for="column in surgeonColumns"
                      :key="column.key"
                      class="grid-cell"
                      @click="startSurgeonEdit(surgeon, column.key)"
                    >
                      <input
                        v-if="isSurgeonEditing(surgeon, column.key)"
                        :ref="el => el && el.focus()"
                        v-model="surgeon[column.key]"
                        class="cell-input"
                        :type="column.key === 'email' ? 'email' : 'text'"
                        @input="surgeon._dirty = true"
                        @blur="stopSurgeonEdit"
                        @keydown.enter="stopSurgeonEdit"
                        @keydown.escape="stopSurgeonEdit"
                      >
                      <span
                        v-else
                        class="cell-value"
                        :class="{ 'cell-value--empty': !surgeon[column.key] }"
                      >
                        {{ surgeon[column.key] || (column.required ? '(required)' : '—') }}
                      </span>
                    </td>
                    <td class="grid-cell select-cell">
                      <VAutocomplete
                        v-model="surgeon.specialist_area_ids"
                        :items="specialistAreas"
                        item-value="id"
                        item-title="name"
                        density="compact"
                        variant="plain"
                        hide-details
                        multiple
                        chips
                        closable-chips
                        placeholder="Select…"
                        class="cell-autocomplete"
                        @update:model-value="surgeon._dirty = true"
                      />
                    </td>
                    <td class="grid-cell">
                      {{ surgeon.secretaries?.map(secretary => secretary.full_name).filter(Boolean).join(', ') || '—' }}
                    </td>
                    <td class="col-actions">
                      <VBtn
                        v-if="surgeon._dirty"
                        icon
                        size="small"
                        variant="text"
                        color="success"
                        :loading="surgeonSaving[surgeon._key]"
                        @click.stop="saveSurgeonRow(surgeon)"
                      >
                        <VIcon size="18">
                          mdi-content-save-outline
                        </VIcon>
                      </VBtn>
                      <VBtn
                        icon
                        size="small"
                        variant="text"
                        color="error"
                        :loading="surgeonDeleting[surgeon._key]"
                        @click.stop="deleteSurgeonRow(surgeon)"
                      >
                        <VIcon size="18">
                          mdi-delete-outline
                        </VIcon>
                      </VBtn>
                    </td>
                  </tr>
                  <tr v-if="surgeonRows.length === 0">
                    <td
                      :colspan="surgeonColumns.length + 2"
                      class="text-center text-medium-emphasis pa-8"
                    >
                      No surgeons found.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </VCardText>
          <VDivider />
          <VCardActions class="pa-3 justify-end">
            <VBtn
              color="primary"
              variant="flat"
              prepend-icon="mdi-plus"
              @click="addSurgeonRow"
            >
              Add Surgeon
            </VBtn>
          </VCardActions>
        </VCard>
      </VWindowItem>

      <!-- ─── TAB 3: QUICK LISTINGS ────────────────────────────────────────── -->
      <VWindowItem value="listings">
        <VCard>
          <VCardText class="pa-0">
            <div
              v-if="listingLoading"
              class="d-flex justify-center pa-8"
            >
              <VProgressCircular
                indeterminate
                color="primary"
              />
            </div>

            <div
              v-else
              class="grid-wrapper"
            >
              <table class="data-grid">
                <thead>
                  <tr>
                    <th style="min-width:120px">
                      Surgeon GMC
                    </th>
                    <th style="min-width:180px">
                      Hospital *
                    </th>
                    <th style="min-width:200px">
                      Specialist Areas
                    </th>
                    <th style="min-width:140px">
                      Date *
                    </th>
                    <th style="min-width:100px">
                      Time
                    </th>
                    <th style="min-width:100px">
                      Duration (min)
                    </th>
                    <th style="min-width:180px">
                      Assign to
                    </th>
                    <th class="col-actions" />
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in listingRows"
                    :key="row._key"
                    :class="{ 'row--saved': row._saved }"
                  >
                    <!-- Surgeon GMC -->
                    <td class="grid-cell">
                      <input
                        v-model="row.surgeon_gmc_number"
                        class="cell-input"
                        placeholder="e.g. 1234567"
                        @input="row._dirty = true"
                      >
                    </td>
                    <!-- Hospital -->
                    <td class="grid-cell select-cell">
                      <VAutocomplete
                        v-model="row.hospital_id"
                        :items="hospitals"
                        item-value="id"
                        item-title="name"
                        density="compact"
                        variant="plain"
                        hide-details
                        :placeholder="row.hospital_id ? hospitalName(row.hospital_id) : 'Select…'"
                        class="cell-autocomplete"
                        @update:model-value="row._dirty = true"
                      />
                    </td>
                    <!-- Specialist areas -->
                    <td class="grid-cell select-cell">
                      <VAutocomplete
                        v-model="row.specialist_area_ids"
                        :items="specialistAreas"
                        item-value="id"
                        item-title="name"
                        density="compact"
                        variant="plain"
                        hide-details
                        multiple
                        chips
                        closable-chips
                        placeholder="Select…"
                        class="cell-autocomplete"
                        @update:model-value="row._dirty = true"
                      />
                    </td>
                    <!-- Date -->
                    <td class="grid-cell">
                      <input
                        v-model="row.scheduled_date"
                        type="date"
                        class="cell-input"
                        @change="row._dirty = true"
                      >
                    </td>
                    <!-- Time -->
                    <td class="grid-cell">
                      <input
                        v-model="row.scheduled_time"
                        type="time"
                        class="cell-input"
                        @change="row._dirty = true"
                      >
                    </td>
                    <!-- Duration -->
                    <td class="grid-cell">
                      <input
                        v-model.number="row.duration_minutes"
                        type="number"
                        min="30"
                        step="15"
                        class="cell-input"
                        @input="row._dirty = true"
                      >
                    </td>
                    <!-- Assign to -->
                    <td class="grid-cell select-cell">
                      <VAutocomplete
                        v-model="row.assigned_anaesthetist_id"
                        :items="managedAnaesthetists"
                        item-value="id"
                        item-title="full_name"
                        density="compact"
                        variant="plain"
                        hide-details
                        clearable
                        placeholder="Optional…"
                        class="cell-autocomplete"
                        @update:model-value="row._dirty = true"
                      />
                    </td>
                    <!-- Actions -->
                    <td class="col-actions">
                      <div class="d-flex align-center ga-1">
                        <VBtn
                          icon
                          size="small"
                          variant="flat"
                          color="success"
                          :loading="row._saving"
                          :disabled="row._saved"
                          title="Create listing"
                          @click="saveListingRow(row)"
                        >
                          <VIcon size="18">
                            {{ row._saved ? 'mdi-check' : 'mdi-send-outline' }}
                          </VIcon>
                        </VBtn>
                        <VBtn
                          icon
                          size="small"
                          variant="text"
                          color="error"
                          title="Remove row"
                          @click="removeListingRow(row._key)"
                        >
                          <VIcon size="18">
                            mdi-close
                          </VIcon>
                        </VBtn>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="listingRows.length === 0">
                    <td
                      colspan="8"
                      class="text-center text-medium-emphasis pa-8"
                    >
                      Click "Add Row" to start entering listings quickly.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </VCardText>

          <VDivider />

          <VCardActions class="pa-3 justify-end">
            <VBtn
              color="primary"
              variant="flat"
              prepend-icon="mdi-plus"
              @click="addListingRow"
            >
              Add Row
            </VBtn>
          </VCardActions>
        </VCard>
      </VWindowItem>
    </VWindow>

    <VSnackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      location="bottom end"
      :timeout="3000"
    >
      {{ snackbar.text }}
    </VSnackbar>
  </div>
</template>

<style scoped>
.external-contacts-page {
  padding-block: 0.5rem 0;
}

.grid-wrapper {
  overflow-x: auto;
}

.data-grid {
  border-collapse: collapse;
  font-size: 0.875rem;
  inline-size: 100%;
}

.data-grid thead th {
  background: rgb(var(--v-theme-surface));
  border-block-end: 2px solid rgba(var(--v-border-color), var(--v-border-opacity));
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  padding-block: 10px;
  padding-inline: 12px;
  text-align: start;
  text-transform: uppercase;
  white-space: nowrap;
}

.data-grid tbody tr {
  border-block-end: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  transition: background 0.15s;
}

.data-grid tbody tr:hover { background: rgba(var(--v-theme-primary), 0.04); }
.data-grid tbody tr.row--dirty { background: rgba(var(--v-theme-warning), 0.06); }
.data-grid tbody tr.row--new { background: rgba(var(--v-theme-success), 0.06); }
.data-grid tbody tr.row--saved { background: rgba(var(--v-theme-success), 0.10); }
.data-grid tbody tr.row--registered { cursor: default; }
.data-grid tbody tr.row--registered .grid-cell { cursor: default; }

.grid-cell {
  cursor: text;
  min-block-size: 42px;
  padding-block: 4px;
  padding-inline: 8px;
  vertical-align: middle;
}

.col-expand {
  inline-size: 44px;
  padding-inline: 4px;
  text-align: center;
}

.expanded-row {
  background: rgba(var(--v-theme-primary), 0.04);
}

.expanded-content {
  padding: 12px 16px;
}

.select-cell { padding-inline: 4px; }

.cell-input {
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: rgb(var(--v-theme-on-surface));
  font-size: 0.875rem;
  inline-size: 100%;
  min-inline-size: 80px;
  padding-block: 4px;
  padding-inline: 6px;
  transition: border-color 0.15s, background 0.15s;
}

.cell-input:focus {
  border-color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-surface));
  outline: none;
}

.cell-autocomplete { min-inline-size: 120px; }

.cell-value {
  display: block;
  max-inline-size: 200px;
  overflow: hidden;
  padding-block: 4px;
  padding-inline: 2px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cell-value--empty {
  color: rgba(var(--v-theme-on-surface), 0.35);
  font-style: italic;
}

.col-actions {
  padding-block: 4px;
  padding-inline: 8px;
  white-space: nowrap;
}
</style>
